import { useTheme } from '@/contexts/ThemeContext';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';

export type LuxModalVariant = 'default' | 'success' | 'warning' | 'danger';

export interface LuxModalHeaderProps {
  /** Contenu du header (titre, etc.) */
  children: ReactNode;
  /** Variante visuelle */
  variant?: LuxModalVariant;
  /** Afficher le bouton de fermeture */
  showCloseButton?: boolean;
  /** Callback de fermeture */
  onClose?: () => void;
  /** Classes CSS additionnelles */
  className?: string;
  /** Actions additionnelles (ex: bouton print) affichées à gauche du bouton de fermeture */
  actions?: ReactNode;
}

// Gradient classes are now handled via CSS custom classes in index.css
// (modal-header-gradient / .dark .modal-header-gradient)

export function LuxModalHeader({
  children,
  variant: _variant = 'default',
  showCloseButton = true,
  onClose,
  className = '',
  actions,
}: LuxModalHeaderProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  return (
    <div
      className={`px-6 py-4 rounded-t-lg border-b modal-header-gradient ${isDark ? 'border-slate-700' : 'border-gray-200'} ${className}`}
      data-lux-modal-header
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 min-w-0">{children}</div>
        {actions && <div className="flex-shrink-0 flex items-center gap-2">{actions}</div>}
        {showCloseButton && onClose && (
          <button
            type="button"
            onClick={onClose}
            className={
              isDark
                ? 'flex-shrink-0 p-2 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-900'
                : 'flex-shrink-0 p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-200 hover:bg-opacity-80 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
            }
            aria-label="Fermer"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
}
